import React from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowLeft } from "lucide-react";

export default function Terms() {
  return (
    <main className="max-w-3xl mx-auto px-5 py-16" data-testid="terms-page">
      <Link to="/" className="inline-flex items-center gap-2 fl-mono text-[11px] tracking-widest uppercase text-slate-400 hover:text-emerald-500 mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Fliboe
      </Link>

      <div className="flex items-center gap-3 mb-2">
        <FileText className="w-6 h-6 text-emerald-500" />
        <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-emerald-500">// Legal</div>
      </div>
      <h1 className="fl-display text-4xl sm:text-5xl font-extrabold tracking-tighter">Terms of Service</h1>
      <p className="text-slate-400 mt-3 text-sm">Last updated: February 2026</p>

      <div className="prose prose-invert mt-10 space-y-8 text-slate-300 text-sm leading-relaxed">
        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">1. Welcome to Fliboe</h2>
          <p>
            Fliboe ("we", "us", "the platform") is an internet radio platform that lets anyone launch and
            broadcast their own streaming station. By creating an account, uploading content, or listening
            on Fliboe, you ("you", "broadcaster", "listener") agree to these Terms.
          </p>
        </section>

        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">2. Accounts</h2>
          <p>
            You must be 13 or older to create an account. You're responsible for your password and any
            activity that happens under your account. Don't share your login. If you spot unauthorized
            use, contact us via the in-app AI Support chat right away.
          </p>
        </section>

        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">3. Content you upload (THE BIG ONE)</h2>
          <p>
            <strong className="text-emerald-400">You — and only you — are responsible for the music, talk shows, cover art,
            and anything else you upload to your station.</strong> By uploading, you affirm that:
          </p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>You own the rights, OR you have a license, OR the content is in the public domain.</li>
            <li>The content does not infringe anyone else's copyright, trademark, publicity, or privacy rights.</li>
            <li>The content is not illegal, obscene, threatening, defamatory, or hate speech.</li>
          </ul>
          <p className="mt-2">
            If a rights-holder contacts us with a takedown request (DMCA or equivalent), we'll remove the
            content and may suspend your station. Repeat infringers will be banned. We reserve the right
            to remove any content at our sole discretion.
          </p>
        </section>

        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">4. The Tip Jar — 100% yours</h2>
          <p>
            When you add a Tip Jar URL (PayPal, Cash App, Venmo, etc.) to your station, listeners pay
            you directly through that external service. <strong>Fliboe never touches that money.</strong> We don't
            process it, don't see it, don't take a cut. Disputes, refunds, taxes, and reporting on
            tip income are entirely between you, the listener, and your payment provider.
          </p>
        </section>

        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">5. Plans & pricing</h2>
          <p>
            The Free plan is live now and free forever. Premium and Broadcaster paid plans are in
            development and currently accept waitlist signups only — no charges are being processed
            at this time. When paid plans launch, we'll email you with clear terms before any
            payment is taken.
          </p>
        </section>

        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">6. AI features</h2>
          <p>
            Fliboe uses third-party AI services (OpenAI, etc.) to generate the AI DJ voice drops
            between songs and to power the support chat. By using these features you understand that
            short text snippets about your station (name, track titles, genre) are sent to those
            providers to generate audio and replies. We don't sell your data and we don't train models on it.
          </p>
        </section>

        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">7. Termination</h2>
          <p>
            You can delete your account anytime via the AI Support chat. We may suspend or terminate
            accounts that violate these Terms (copyright infringement, abuse, spam, etc.) without notice.
          </p>
        </section>

        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">8. Disclaimers</h2>
          <p>
            Fliboe is provided "as is", without warranties of any kind. We do not guarantee uninterrupted
            service or that the platform will be error-free. To the maximum extent allowed by law, we are
            not liable for any indirect, incidental, or consequential damages arising from your use of
            the platform.
          </p>
        </section>

        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">9. Privacy</h2>
          <p>
            We collect the bare minimum needed to run the platform: your email, name, station data,
            anonymous listener counts, and optionally a profile picture. We use HTTPS everywhere. We
            don't sell your data. Push notification endpoints and referral data are stored to make those
            features work — and that's it.
          </p>
        </section>

        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">10. Changes to these Terms</h2>
          <p>
            We may update these Terms as the platform evolves. Material changes will be announced
            in-app and via email when paid plans are active. Continued use after changes means you accept
            the updated Terms.
          </p>
        </section>

        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">11. Contact</h2>
          <p>
            Questions, takedown requests, or general feedback: use the AI Support chat at the bottom-right
            of every page. It's the official channel and a human team member reviews escalations.
          </p>
        </section>
      </div>
    </main>
  );
}
